// GL HEALTH (gl-watchdog, 2026-11-02): the running client counts GL errors and three's own GL complaints,
// cheaply, and every telemetry row carries the window's counts (`gl`, PROTOCOL.md telemetry) — so a draw a
// device's GL drops silently (the Quest's validating decoder: "Source and destination textures of the draw are
// the same", INVALID_OPERATION, the draw skipped; ANGLE on a desktop draws it) shows in every real session.
//
// THE SAMPLE: ONE `gl.getError()` drain (≤ 8 codes: the flags are sticky, one per code, until read) at most once
// per `every` ms (5 s = the telemetry row's period: one drain per row, nothing lost — the flags wait), never per draw.
// getError is a synchronous round trip to the GPU process: it waits until the
// decoder has run EVERY queued command — at a frame start that is the previous frame (measured on the box's probe
// Chrome: 40 ms median, 320 ms in the shader warm). So it waits for a quiet decoder: at a frame's end a
// `fenceSync` (+ flush); between frames its status is polled (getSyncParameter: the browser's cached value, no round
// trip; a timer, 1 ms); once SIGNALED (the GPU finished that frame, nothing of the next one is queued) the drain runs
// there, in the gap. Not signaled by the next frame start (a GPU-bound frame) = dropped (`miss`), deleted, tried
// again at that frame's end: a busy GPU loses samples, not frames. A device GPU-bound for `force` ms (60 s) without
// a drain gets ONE forced drain at a frame start (`forced`; it waits ≈ one GPU frame: the box's probe Chrome at 15 fps
// measured 66 ms — at 30 s that alone was 0.1 ms / frame there) — the flags are sticky, so nothing is lost, only late.
// A signaled drain is not free on a saturated box either (the GPU process serves the compositor too: IWER on the box,
// 22 fps, 8–10 ms a drain = 0.42 ms / frame at 1 s; hence 5 s). The drain's own time is measured and sent
// (`ms` mean / `ms_max`): the telemetry states its cost. `s` = drains, `err_s` = drains that found ≥ 1 error;
// `op` / `val` / `enum` / `fb` / `oom` / `other` = codes drained per kind.
// What it cannot see: a decoder RENDER WARNING with no GL error ("texture bound to texture unit N is not
// renderable": the draw runs, the sampler reads black). A page sees no browser console line (no API reaches
// it); tools/questsmoke.mjs reads them over CDP (Log.entryAdded) and audits the draws in JS.
// THREE: three's console.error / console.warn lines starting 'THREE.' are counted (`prog` = shader / program
// failures 'THREE.WebGLProgram', `three` = the rest), the call itself passed through unchanged.
// `restored` = webglcontextrestored events (the losses stay `ctx_lost_n`, main.js watchContext).
const CODES = { 0x0500: 'enum', 0x0501: 'val', 0x0502: 'op', 0x0505: 'oom', 0x0506: 'fb' };
const CONTEXT_LOST_WEBGL = 0x9242;
const SYNC_GPU_COMMANDS_COMPLETE = 0x9117, SYNC_STATUS = 0x9114, SIGNALED = 0x9119;
export const GL_KEYS = ['s', 'err_s', 'op', 'val', 'enum', 'fb', 'oom', 'other', 'miss', 'forced', 'ms', 'ms_max', 'restored', 'prog', 'three'];
const zero = () => ({ s: 0, err_s: 0, op: 0, val: 0, enum: 0, fb: 0, oom: 0, other: 0, miss: 0, forced: 0, ms_sum: 0, ms_max: 0, restored: 0, prog: 0, three: 0 });

export function createGlWatch(gl, { every = 5000, force = 60000, clock = () => performance.now(), later = (f) => setTimeout(f, 1) } = {}) {
  const win = zero(), tot = zero();
  let last = -Infinity, first = null;   // first: the first error seen (code, t)
  let drained = clock();                 // the last drain (or the start): the forced drain's clock
  let fence = null;
  const add = (k, n = 1) => { win[k] += n; tot[k] += n; };
  const done = () => { try { return gl.getSyncParameter(fence, SYNC_STATUS) === SIGNALED; } catch { return false; } };
  const drop = () => { try { gl.deleteSync(fence); } catch { /* lost */ } fence = null; };
  function drain() {
    drop();
    const a = clock();
    last = a; drained = a;
    if (gl.isContextLost()) return;
    let hit = 0;
    for (let i = 0; i < 8; i++) {
      const e = gl.getError();
      if (!e || e === CONTEXT_LOST_WEBGL) break;
      hit++;
      add(CODES[e] || 'other');
      if (!first) first = { code: e, t: Math.round(a) };
    }
    const ms = clock() - a;
    add('s');
    if (hit) add('err_s');
    win.ms_sum += ms; tot.ms_sum += ms;
    if (ms > win.ms_max) win.ms_max = ms;
    if (ms > tot.ms_max) tot.ms_max = ms;
  }
  function poll() {
    if (!fence) return;
    if (done()) drain(); else later(poll);
  }
  // a frame's end (after its render): the fence when a sample is due
  function frameEnd(now = clock()) {
    if (fence || now - last < every || !gl || gl.isContextLost()) return;
    fence = gl.fenceSync(SYNC_GPU_COMMANDS_COMPLETE, 0);
    if (!fence) return;
    gl.flush();
    later(poll);
  }
  // the next frame's start: a fence still pending was a GPU-bound frame — dropped (a stall otherwise), unless no
  // drain came for `force` ms: then this one waits (forced)
  function frameStart(now = clock()) {
    if (!fence) return;
    if (done()) drain();
    else if (now - drained >= force) { add('forced'); drain(); } else { drop(); add('miss'); }
  }
  const out = (o) => ({
    s: o.s, err_s: o.err_s, op: o.op, val: o.val, enum: o.enum, fb: o.fb, oom: o.oom, other: o.other, miss: o.miss, forced: o.forced,
    ms: o.s ? Math.round((o.ms_sum / o.s) * 1000) / 1000 : null, ms_max: Math.round(o.ms_max * 1000) / 1000,
    restored: o.restored, prog: o.prog, three: o.three,
  });
  // the telemetry block of the window since the last call (then reset)
  function window() {
    const w = out(win);
    Object.assign(win, zero());
    return w;
  }
  // since load (dev: __vr.glwatch(); tools/questsmoke.mjs)
  const state = () => ({ ...out(tot), first, every });
  const restored = () => add('restored');
  // three's console complaints ('THREE.' prefix), counted; the console call itself unchanged
  function watchConsole(con = console) {
    for (const m of ['error', 'warn']) {
      const orig = con[m];
      if (typeof orig !== 'function' || orig.__glw) continue;
      const f = function (...a) {
        const s = a[0];
        if (typeof s === 'string' && s.startsWith('THREE.')) add(s.startsWith('THREE.WebGLProgram') ? 'prog' : 'three');
        return orig.apply(this, a);
      };
      f.__glw = true;
      con[m] = f;
    }
  }
  return { frameEnd, frameStart, window, state, restored, watchConsole };
}
